import { Reason } from './Reason.js';
import { ActionButton, ActionInput, ActionSelect } from '../parts/index.js';
// The league register on the admin Snowflake console (pipeline RC18, B2372; Mike by pop-up, 8 Oct 2026 about 09:55 CEST,
// "A Leagues list in admin"): which league each competition name in the viewing and fixtures data is read as, and which
// names are one-offs read apart in both years. The night's run places names by rule and proposes the ones it cannot be
// sure of; a person accepts a proposal, keeps a name where it is, moves it, says what kind it is, or returns it to the
// rule, as themselves. The database decides who may; this page only asks and shows the answer.
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { ApiError, Unauthenticated, type Store } from '../api.js';
import { admin, type KnownLeague, type LeagueKind, type LeagueListView, type LeaguePressVerb, type LeagueRow } from './api.js';
import { n } from './format.js';

export const KIND_WORDS: Record<LeagueKind, string> = {
  league: 'A league',
  cup: 'A cup held every two or four years',
  qualifiers: 'Qualifiers',
  games: 'A multi-sport games',
  series: 'A touring series',
  tour_stop: 'One stop of a tour',
};
const KINDS = Object.keys(KIND_WORDS) as LeagueKind[];

function said(e: unknown): string {
  if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return 'signing you in again…'; }
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}

const sportOf = (league: string) => league.split('|')[0] ?? '';
const nameOf = (league: string) => league.split('|').slice(1).join('|');
const key = (r: { sport: string; competition: string }) => `${r.sport}\t${r.competition}`;

function placed(r: LeagueRow) {
  if (r.basis === 'person') return <>placed by {r.decidedBy ?? 'a person'}{r.decidedAt ? <><br /><span className="dim">{r.decidedAt.slice(0, 10)}</span></> : null}</>;
  return <span className="dim">by rule{r.how ? `: ${r.how}` : ''}</span>;
}

function Move({ canSave, store, row, busy, press, close }: {
  canSave: boolean; store: Store; row: LeagueRow; busy: boolean; close: () => void;
  press: (r: LeagueRow, verb: LeaguePressVerb, extra?: { league?: string; kind?: LeagueKind }) => void;
}) {
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<KnownLeague[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const find = (e: FormEvent) => {
    e.preventDefault(); setError(null); setFound(null);
    if (term.trim().length < 2) { setError('Type at least two letters of the league\'s name.'); return; }
    admin.knownLeagues(store.tenantId, term.trim()).then((r) => setFound(r.leagues)).catch((x: unknown) => setError(said(x)));
  };
  const sport = sportOf(row.league);
  const typed = term.trim();
  return (
    <form className="admin-find" onSubmit={find}>
      <ActionInput reading aria-label={`league for ${row.competition}`} placeholder="league name" value={term} autoFocus onChange={(e) => setTerm(e.target.value)} />
      <ActionButton reading className="small" type="submit">Find</ActionButton>
      <ActionButton reading className="small quiet" type="button" onClick={close}>Close</ActionButton>
      {error ? <span className="admin-says parked">{error}</span> : null}
      {found !== null && found.length === 0 ? <span className="dim small">No league of that name in the register.</span> : null}
      {found ? (
        <div className="admin-actions">
          {found.filter((k) => k.league !== row.league).map((k) => (
            <ActionButton key={k.league} className="small" type="button" disabled={busy || !canSave} onClick={() => press(row, 'move', { league: k.league })}>
              {nameOf(k.league)} <span className="dim">· {sportOf(k.league)} · {n(k.names)} name{k.names === 1 ? '' : 's'}</span>
            </ActionButton>
          ))}
          {typed.length >= 2 && !found.some((k) => nameOf(k.league).toLowerCase() === typed.toLowerCase()) ? (
            <ActionButton className="small quiet" type="button" disabled={busy || !canSave} onClick={() => press(row, 'move', { league: `${sport}|${typed}` })}>
              A new league, &ldquo;{typed}&rdquo;, under {sport}
            </ActionButton>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}

export function Leagues({ store }: { store: Store }) {
  const [data, setData] = useState<LeagueListView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const canSave = reason.trim().length > 0;
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [term, setTerm] = useState('');
  const [asked, setAsked] = useState('');
  const [turn, setTurn] = useState(0);
  const reload = useCallback(() => setTurn((x) => x + 1), []);
  useEffect(() => {
    let cancelled = false;
    admin.leagues(store.tenantId, asked).then((r) => { if (!cancelled) { setData(r.leagues); setError(null); } }).catch((e: unknown) => { if (!cancelled) setError(said(e)); });
    return () => { cancelled = true; };
  }, [store.tenantId, asked, turn]);
  const press = (r: LeagueRow, verb: LeaguePressVerb, extra: { league?: string; kind?: LeagueKind } = {}) => {
    if (!canSave || busy !== null) return;
    setBusy(key(r)); setError(null); setDone(null);
    admin.pressLeague(store.tenantId, { sport: r.sport, competition: r.competition, verb, ...extra, reason: reason.trim() })
      .then((x) => {
        setReason('');
        const p = x.pressed;
        setDone(`${r.competition || '(no name)'} is now read as ${nameOf(p.league)} (${sportOf(p.league)})${p.kind === 'league' ? '' : `, ${KIND_WORDS[p.kind].toLowerCase()}, read apart in both years`}. The readings follow on the next night's run.`);
        setMoving(null); reload();
      })
      .catch((e: unknown) => setError(said(e))).finally(() => setBusy(null));
  };
  const search = (e: FormEvent) => { e.preventDefault(); setDone(null); setAsked(term.trim().length >= 2 ? term.trim() : ''); };
  const c = data?.counts;
  return (
    <>
      <div className="admin-head"><h2>Leagues</h2>
        <span>{c ? `${n(c.names)} names read as ${n(c.leagues)} leagues · ${n(c.proposed)} proposed and waiting for a person · ${n(c.byPerson)} placed by a person · ${n(c.oneOff)} one-offs read apart` : 'reading…'}</span></div>
      <Reason value={reason} onChange={setReason} busy={busy !== null} />
      <p className="admin-note">
        Which league each competition name in the viewing and fixtures data is read as. The same league goes by different names
        across years and feeds; a one-off (a cup, qualifiers, a games, a touring series, a tour stop) is left out of both years
        when a client&rsquo;s use is read against its own pattern. Each night the run places new names by rule and proposes the
        ones it cannot be sure of; a proposal counts only once a person accepts it.
      </p>
      <form className="admin-find" onSubmit={search}>
        <ActionInput reading aria-label="find a competition or league" placeholder="competition or league" value={term} onChange={(e) => setTerm(e.target.value)} />
        <ActionButton reading className="small" type="submit">Find</ActionButton>
        {asked ? <ActionButton reading className="small quiet" type="button" onClick={() => { setTerm(''); setAsked(''); setDone(null); }}>Show what waits for a person</ActionButton> : null}
      </form>
      {error ? <p className="admin-error" role="alert">{error} <ActionButton reading type="button" className="admin-retry" onClick={reload}>Try again</ActionButton></p> : null}
      {done ? <p className="admin-says ok" role="status">{done}</p> : null}
      {data ? (
        <div className="admin-scroll">
          <table className="admin">
            <thead><tr><th>Name in Snowflake</th><th>Read as</th><th>Placed</th><th className="num">Hours, 12 months</th><th /></tr></thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={key(r)} className={r.proposedLeague ? undefined : 'decided'}>
                  <td><strong>{r.competition || 'no name in Snowflake'}</strong>
                    <div className="dim small">{r.sport || 'no sport'}{r.isNew ? ' · new this month' : ''}</div></td>
                  <td>{nameOf(r.league)}<div className="dim small">{sportOf(r.league)}{r.kind === 'league' ? '' : ` · ${KIND_WORDS[r.kind].toLowerCase()}, read apart`}</div>
                    {r.proposedLeague ? <div className="small">The run proposes <strong>{nameOf(r.proposedLeague)}</strong>{r.proposedWhy ? <span className="dim">: {r.proposedWhy}</span> : null}</div> : null}</td>
                  <td>{placed(r)}</td>
                  <td className="num">{r.hours12m > 0 ? n(r.hours12m) : <span className="dim">none</span>}</td>
                  <td>
                    <div className="admin-actions">
                      {r.proposedLeague ? (
                        <>
                          <ActionButton className="small" type="button" disabled={busy !== null || !canSave} onClick={() => press(r, 'accept')}>Accept</ActionButton>
                          <ActionButton className="small danger" type="button" disabled={busy !== null || !canSave} onClick={() => press(r, 'keep')}>Keep as it is</ActionButton>
                        </>
                      ) : null}
                      <ActionButton className="small quiet" type="button" disabled={busy !== null} onClick={() => setMoving(moving === key(r) ? null : key(r))}>
                        {moving === key(r) ? 'Close' : 'Move…'}
                      </ActionButton>
                      <ActionSelect aria-label={`kind of ${r.competition}`} className="small" value={r.kind} disabled={busy !== null || !canSave}
                        onChange={(e) => press(r, 'kind', { kind: e.target.value as LeagueKind })}>
                        {KINDS.map((k) => <option key={k} value={k}>{KIND_WORDS[k]}</option>)}
                      </ActionSelect>
                      {r.basis === 'person' ? <ActionButton className="small quiet" type="button" disabled={busy !== null || !canSave} onClick={() => press(r, 'rule')}>Return to the rule</ActionButton> : null}
                    </div>
                    {moving === key(r) ? <Move canSave={canSave} store={store} row={r} busy={busy !== null} press={press} close={() => setMoving(null)} /> : null}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 ? (
                <tr><td colSpan={5} className="dim">{data.q ? `No name or league holds “${data.q}”.` : 'Nothing waits for a person: no proposal, no new name this month and nothing placed by hand.'}</td></tr>
              ) : null}
            </tbody>
          </table>
          {data.limited ? <p className="dim small">The first {n(data.rows.length)} are shown; find a name to narrow the list.</p> : null}
        </div>
      ) : null}
    </>
  );
}
